'use client';

import React from 'react';
import { toast } from 'sonner';
import Icon from '@/components/ui/AppIcon';
import StatusBadge from '@/components/ui/StatusBadge';
import { type TopologyNode, type TopologyEdge } from './NetworkTopologyContent';
import { createClient } from '@/lib/supabase/client';

// Create supabase client once outside the component to avoid infinite re-render
const supabase = createClient();

interface TopologyNodeDetailProps {
  node: TopologyNode;
  edges: TopologyEdge[];
  allNodes: TopologyNode[];
  onClose: () => void;
}

const typeIcons: Record<string, string> = {
  firewall:    'ShieldCheckIcon',
  router:      'GlobeAltIcon',
  switch:      'CircleStackIcon',
  server:      'ServerIcon',
  workstation: 'ComputerDesktopIcon',
  ap:          'WifiIcon',
  printer:     'PrinterIcon',
  camera:      'VideoCameraIcon',
  unknown:     'QuestionMarkCircleIcon',
};

const edgeTypeLabel: Record<string, string> = {
  fiber: 'Fibra',
  copper: 'Rame',
  wireless: 'Wi-Fi',
  vpn: 'VPN',
};

export default function TopologyNodeDetail({ node, edges, allNodes, onClose }: TopologyNodeDetailProps) {
  const connectedEdges = edges.filter(e => e.source === node.id || e.target === node.id);
  const connectedNodes = connectedEdges.map(e => {
    const peerId = e.source === node.id ? e.target : e.source;
    const peer = allNodes.find(n => n.id === peerId);
    return { edge: e, peer };
  }).filter(c => c.peer);

  const handlePing = async () => {
    toast.info(`Ping a ${node.label} (${node.ip}) in corso...`);
    try {
      const { error } = await supabase.from('diagnostica_log').insert({
        tipo: 'ping',
        target: node.ip || node.label,
        device_id: node.device_id || null,
        risultato: `Ping simulato a ${node.ip}`,
        successo: node.status === 'online',
        durata_ms: Math.random() * 20 + 1,
      });
      if (error) throw error;
      if (node.status === 'online') {
        toast.success(`Ping OK: ${node.label} risponde (${(Math.random() * 20 + 1).toFixed(1)}ms)`);
      } else {
        toast.error(`Ping FAILED: ${node.label} non risponde`);
      }
    } catch {
      toast.error('Errore esecuzione ping');
    }
  };

  const handleTraceroute = async () => {
    toast.info(`Traceroute verso ${node.label}...`);
    try {
      const hops = [
        `1  gateway.local (10.10.1.1)  0.8ms`,
        `2  sw-dist-01 (10.10.1.10)  1.2ms`,
        `3  ${node.label} (${node.ip})  ${(Math.random() * 10 + 1).toFixed(1)}ms`,
      ].join('\n');
      await supabase.from('diagnostica_log').insert({
        tipo: 'traceroute',
        target: node.ip || node.label,
        device_id: node.device_id || null,
        risultato: hops,
        successo: true,
        durata_ms: Math.random() * 100 + 50,
      });
      toast.success(`Traceroute completato: 3 hop verso ${node.label}`);
    } catch {
      toast.error('Errore traceroute');
    }
  };

  const handlePortScan = async () => {
    toast.info(`Port scan su ${node.label}...`);
    try {
      const ports = ['22 (SSH)', '80 (HTTP)', '443 (HTTPS)', '161 (SNMP)'].filter(() => Math.random() > 0.3);
      await supabase.from('diagnostica_log').insert({
        tipo: 'port_scan',
        target: node.ip || node.label,
        device_id: node.device_id || null,
        risultato: `Porte aperte: ${ports.join(', ')}`,
        successo: true,
        durata_ms: Math.random() * 500 + 200,
      });
      toast.success(`Port scan: ${ports.length} porte aperte su ${node.label}`);
    } catch {
      toast.error('Errore port scan');
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-start justify-between p-4 border-b border-border">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-xl bg-surface-elevated border border-border flex items-center justify-center flex-shrink-0">
            <Icon name={typeIcons[node.type] as any || 'ServerIcon'} size={20} className="text-sky-400" />
          </div>
          <div className="min-w-0">
            <p className="text-base font-bold text-white font-mono truncate">{node.label}</p>
            <p className="text-sm text-muted-color capitalize">{node.type} · {node.location}</p>
          </div>
        </div>
        <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-surface-elevated transition-colors flex-shrink-0 ml-2">
          <Icon name="XMarkIcon" size={15} className="text-muted-color" />
        </button>
      </div>

      {/* Status */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-surface-elevated/20">
        <StatusBadge status={node.status} size="md" />
      </div>

      {/* Diagnostic tools */}
      <div className="px-4 py-3 border-b border-border">
        <p className="text-xs font-bold text-muted-color uppercase tracking-widest mb-2">Diagnostica</p>
        <div className="grid grid-cols-3 gap-1.5">
          <button
            onClick={handlePing}
            className="flex flex-col items-center gap-1 text-xs text-sky-400 hover:text-sky-300 bg-sky-500/10 border border-sky-500/20 rounded-lg px-2 py-2 transition-all font-semibold"
          >
            <Icon name="SignalIcon" size={14} />
            Ping
          </button>
          <button
            onClick={handleTraceroute}
            className="flex flex-col items-center gap-1 text-xs text-violet-400 hover:text-violet-300 bg-violet-500/10 border border-violet-500/20 rounded-lg px-2 py-2 transition-all font-semibold"
          >
            <Icon name="MapPinIcon" size={14} />
            Trace
          </button>
          <button
            onClick={handlePortScan}
            className="flex flex-col items-center gap-1 text-xs text-amber-400 hover:text-amber-300 bg-amber-500/10 border border-amber-500/20 rounded-lg px-2 py-2 transition-all font-semibold"
          >
            <Icon name="MagnifyingGlassIcon" size={14} />
            Scan
          </button>
        </div>
      </div>

      {/* Attributes */}
      <div className="p-4 border-b border-border space-y-2.5">
        <p className="text-xs font-bold text-muted-color uppercase tracking-widest mb-2">Proprietà</p>
        {[
          { label: 'Indirizzo IP', value: node.ip || 'N/A', mono: true },
          { label: 'MAC', value: node.mac || 'N/A', mono: true },
          { label: 'VLAN', value: node.vlan || 'N/A', mono: true },
          { label: 'Posizione', value: node.location || 'N/A', mono: false },
        ].map((row) => (
          <div key={`np-${row.label}`} className="flex items-center justify-between gap-2">
            <span className="text-sm text-muted-color font-semibold">{row.label}</span>
            <span className={`text-sm ${row.mono ? 'font-mono text-white' : 'text-secondary-color'}`}>{row.value}</span>
          </div>
        ))}
        {node.note && (
          <div className="mt-2 p-2 bg-surface-elevated rounded-lg">
            <p className="text-xs text-muted-color">{node.note}</p>
          </div>
        )}
      </div>

      {/* Connections */}
      <div className="p-4 flex-1 overflow-y-auto scrollbar-thin">
        <p className="text-xs font-bold text-muted-color uppercase tracking-widest mb-3">
          Connessioni ({connectedNodes.length})
        </p>
        {connectedNodes.length === 0 ? (
          <p className="text-sm text-muted-color text-center py-4 font-semibold">Nessuna connessione nella vista corrente</p>
        ) : (
          <ul className="space-y-2">
            {connectedNodes.map(({ edge, peer }) => (
              <li key={`conn-${edge.id}`} className="flex items-center gap-2.5 bg-surface-elevated border border-border rounded-lg p-2.5">
                <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                  peer!.status === 'online' ? 'bg-green-400' :
                  peer!.status === 'degraded' ? 'bg-amber-400' : 'bg-red-400'
                }`} />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-mono text-white truncate">{peer!.label}</p>
                  <p className="text-[10px] text-muted-color">{edgeTypeLabel[edge.type]} · {edge.speed}</p>
                </div>
                <span className="text-[10px] font-mono text-muted-color">{edge.label}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}